import { api } from "@/api";
import type {
  ApiBlogCategory,
  ApiBlogCluster,
  ApiClusterHub,
  ApiBlogPost,
  ApiBlogPostSummary,
  ApiPaginatedPosts,
  ApiUploadedImage,
} from "@/shared/blog/blogApiShapes";
import {
  mapCategory,
  mapCluster,
  mapPaginated,
  mapPost,
  mapSummary,
  toUpsertBody,
  toUpsertClusterBody,
} from "@/shared/blog/blogMappers";
import type {
  BlogPublicService,
  BlogService,
  ListAllParams,
  ListPublishedParams,
  UploadedImage,
} from "@/shared/blog/blogService.contract";
import type {
  BlogCluster,
  BlogCategory,
  BlogAuthor,
  BlogPost,
  BlogPostChecks,
  BlogPostSummary,
  PaginatedPosts,
  PostStatus,
  UpsertClusterInput,
  UpsertPostInput,
} from "@/shared/blog/blogTypes";

const BACKOFFICE = "v1/backoffice/blog";
const PUBLIC = "v1/blog";

// ── Backoffice (auth) ───────────────────────────────────────────────────────

const listAll = async (params: ListAllParams = {}): Promise<BlogPostSummary[]> => {
  const { data } = await api.get<ApiBlogPostSummary[]>(`${BACKOFFICE}/posts`, {
    params: {
      status: params.status || undefined,
      search: params.search || undefined,
      // Sem isto o filtro de cluster vira enfeite (a etiqueta aparece, a lista
      // não muda) e, pior, o painel de IA recebe pilar e irmãos de clusters
      // alheios para alimentar o gerador e o motor de links.
      cluster_id: params.clusterId || undefined,
    },
  });
  return (data ?? []).map(mapSummary);
};

const getById = async (id: string): Promise<BlogPost> => {
  const { data } = await api.get<ApiBlogPost>(`${BACKOFFICE}/posts/${id}`);
  return mapPost(data);
};

const create = async (input: UpsertPostInput): Promise<BlogPost> => {
  const { data } = await api.post<ApiBlogPost>(`${BACKOFFICE}/posts`, toUpsertBody(input));
  return mapPost(data);
};

const update = async (id: string, input: UpsertPostInput): Promise<BlogPost> => {
  const { data } = await api.put<ApiBlogPost>(`${BACKOFFICE}/posts/${id}`, toUpsertBody(input));
  return mapPost(data);
};

const setStatus = async (
  id: string,
  status: PostStatus,
  scheduledFor?: string
): Promise<BlogPost> => {
  const { data } = await api.patch<ApiBlogPost>(`${BACKOFFICE}/posts/${id}/status`, {
    status,
    scheduled_for: scheduledFor ?? null,
  });
  return mapPost(data);
};

interface ApiBlogPostChecks {
  ready_to_publish: boolean;
  checks: {
    key: string;
    label: string;
    severity: "error" | "warning";
    passed: boolean;
    detail: string | null;
  }[];
}

interface ApiBlogAuthor {
  id: string;
  slug: string;
  name: string;
  role: string | null;
}

const listAuthors = async (): Promise<BlogAuthor[]> => {
  const { data } = await api.get<ApiBlogAuthor[]>(`${BACKOFFICE}/authors`);
  return (data ?? []).map(a => ({ id: a.id, slug: a.slug, name: a.name, role: a.role ?? null }));
};

const getChecks = async (id: string): Promise<BlogPostChecks> => {
  const { data } = await api.get<ApiBlogPostChecks>(`${BACKOFFICE}/posts/${id}/checks`);
  return {
    readyToPublish: data.ready_to_publish,
    checks: data.checks ?? [],
    available: true,
  };
};

const remove = async (id: string): Promise<void> => {
  await api.delete(`${BACKOFFICE}/posts/${id}`);
};

// ── Clusters (E1.4) ─────────────────────────────────────────────────────────

const listClusters = async (): Promise<BlogCluster[]> => {
  const { data } = await api.get<ApiBlogCluster[]>(`${BACKOFFICE}/clusters`);
  return (data ?? []).map(mapCluster);
};

const createCluster = async (input: UpsertClusterInput): Promise<BlogCluster> => {
  const { data } = await api.post<ApiBlogCluster>(`${BACKOFFICE}/clusters`, toUpsertClusterBody(input));
  return mapCluster(data);
};

const updateCluster = async (id: string, input: UpsertClusterInput): Promise<BlogCluster> => {
  const { data } = await api.put<ApiBlogCluster>(
    `${BACKOFFICE}/clusters/${id}`,
    toUpsertClusterBody(input)
  );
  return mapCluster(data);
};

const removeCluster = async (id: string): Promise<void> => {
  await api.delete(`${BACKOFFICE}/clusters/${id}`);
};

const uploadImage = async (file: File): Promise<UploadedImage> => {
  const form = new FormData();
  form.append("file", file);
  // Sem setar Content-Type manualmente: o axios/browser define
  // `multipart/form-data; boundary=...` automaticamente. Forçar o header sem o
  // boundary quebra o parse do multipart no servidor.
  const { data } = await api.post<ApiUploadedImage>(`${BACKOFFICE}/images`, form);
  return { url: data.url };
};

// ── Público ─────────────────────────────────────────────────────────────────

const listPublished = async (params: ListPublishedParams = {}): Promise<PaginatedPosts> => {
  const { data } = await api.get<ApiPaginatedPosts>(`${PUBLIC}/posts`, {
    params: {
      category: params.category || undefined,
      search: params.search || undefined,
      page: params.page ?? 1,
      page_size: params.pageSize ?? 12,
    },
  });
  return mapPaginated(data);
};

const getPublishedBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const { data } = await api.get<ApiBlogPost>(`${PUBLIC}/posts/${slug}`);
    return mapPost(data);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

const listCategories = async (): Promise<BlogCategory[]> => {
  const { data } = await api.get<ApiBlogCategory[]>(`${PUBLIC}/categories`);
  return (data ?? []).map(mapCategory);
};

/**
 * Hub público do cluster (E1.5). O formato vem de `ApiClusterHub`, tipo
 * compartilhado — o SSR consome o mesmo endpoint por outro caminho (fetch, em
 * `ssrPublic.ts`) e os dois precisam concordar.
 */
const getPublishedCluster = async (
  slug: string
): Promise<{ cluster: BlogCluster; posts: BlogPostSummary[] } | null> => {
  try {
    const { data } = await api.get<ApiClusterHub>(
      `${PUBLIC}/clusters/${encodeURIComponent(slug)}`
    );
    if (!data?.cluster) return null;
    return { cluster: mapCluster(data.cluster), posts: (data.posts ?? []).map(mapSummary) };
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

/**
 * 404 (não existe) e 410 (post cancelado pelo botão do post diário — o
 * notifyme responde Gone) são "não encontrado" para a tela: sem retry e com
 * a página de não encontrado, em vez de erro.
 */
const isNotFound = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("response" in error)) return false;
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 404 || status === 410;
};

export const httpBlogService: BlogService & BlogPublicService = {
  listAll,
  getById,
  create,
  update,
  setStatus,
  listAuthors,
  getChecks,
  remove,
  uploadImage,
  listClusters,
  createCluster,
  updateCluster,
  removeCluster,
  listPublished,
  getPublishedBySlug,
  listCategories,
  getPublishedCluster,
};
